import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Form, Alert, Spinner, Table, Navbar, Offcanvas, NavItem } from 'react-bootstrap';
import axios from 'axios';
import ChessGame from './Chess/ChessGame';
import SettingsDisplay from './Chess/SettingsDisplay';
import MatchHistory from './Chess/Replay/MatchHistory';
import ReplayView from './Chess/Replay/ReplayView';
import logo from '../img/evosquare_logo_titled.png';
import Chat from './Chat';
import Timer from './Chess/Timer';
import Lobby from './Lobby';
import LobbyChat from './LobbyChat';
import { API_ENDPOINTS } from '../config';
import { parseJwt } from "./Functions/LoginFunctions";
import useInterval from './Functions/UseInterval';
import onlinePing from './Functions/OnlinePing';


function GameHub() {
    const [activeContent, setActiveContent] = useState('menu');
    const [moveHistory, setMoveHistory] = useState([]);

    const [isRunning, setIsRunning] = useState(() => Boolean(localStorage.getItem('JWToken')));
    const [pingStatus, setPingStatus] = useState(null);
    const [selectedReplay, setSelectedReplay] = useState(null);
    const [lobbyData, setLobbyData] = useState(null);
    const [initialGameState, setInitialGameState] = useState(null);

    const [joinCode, setJoinCode] = useState('');
    const [errorMsg, setErrorMsg] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [myUserId, setMyUserId] = useState(null);
    const [myUserName, setMyUserName] = useState(null);

    const [chatView, setChatView] = useState('global');

    const [settings, setSettings] = useState({
        squareSize: 60,
        pieceFontSize: 45,
        layoutMode: 'responsive',
        timerMinutes: 15
    });

    const [joinPreviewCode, setJoinPreviewCode] = useState('');
    const [joinPreviewLobby, setJoinPreviewLobby] = useState(null);
    const [joinPreviewLoading, setJoinPreviewLoading] = useState(false);
    const [joinPreviewError, setJoinPreviewError] = useState('');
    const [joinPreviewCreator, setJoinPreviewCreator] = useState(null);
    const [joinPreviewJoined, setJoinPreviewJoined] = useState(null);
    const joinPreviewReqRef = useRef(0);

    // --- RESPONSIVE STATES ---
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [showLeftCanvas, setShowLeftCanvas] = useState(false);
    const [showRightCanvas, setShowRightCanvas] = useState(false);

    const navigate = useNavigate();

    // Resize listener
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
        
    }, []);

    const pick = (obj, ...keys) => {
        for (const k of keys) {
            if (obj && obj[k] !== undefined && obj[k] !== null) return obj[k];
        }
        return undefined;
    };

    const isEmptyGuid = (g) => {
        const s = String(g ?? '').trim().toLowerCase();
        if (!s) return true;
        return s === '00000000-0000-0000-0000-000000000000' || s.startsWith('00000000');
    };

    const normalizeInviteCode = (v) => String(v ?? '').replace(/[^a-z0-9]/gi, '').toUpperCase().slice(0, 4);

    const normalizeLobbyInfo = (data) => {
        if (!data) return null;
        return {
            lobbyId: pick(data, 'lobbyId', 'LobbyId'),
            inviteCode: pick(data, 'inviteCode', 'InviteCode'),
            status: pick(data, 'status', 'Status'),
            creator: pick(data, 'creator', 'Creator'),
            joined: pick(data, 'joined', 'Joined', 'joinedUser', 'JoinedUser')
        };
    };

    const normalizePublicUser = (u) => {
        if (!u) return null;
        return {
            uid: pick(u, 'uid', 'Uid'),
            userName: pick(u, 'userName', 'UserName') ?? 'Unknown',
            isOnline: Boolean(pick(u, 'isOnline', 'IsOnline'))
        };
    };

    const getRawToken = () => {
        const t = localStorage.getItem('JWToken');
        if (!t) return null;
        return String(t).replace(/^Bearer\s+/i, '').trim();
    };

    const getAuthHeader = () => {
        const token = getRawToken();
        return {
            headers: {
                Authorization: token ? `Bearer ${token}` : '',
                'Content-Type': 'application/json'
            }
        };
    };

    const isLobbyWaiting = (status) => String(status ?? '').toLowerCase().includes('wait');

    const endpoints = useMemo(() => {
        return {
            createLobby: API_ENDPOINTS.CREATE_LOBBY,
            joinLobby: (code) => `${API_ENDPOINTS.JOIN_LOBBY}?inviteCode=${encodeURIComponent(code)}`,
            getLobbyById: (id) => `${API_ENDPOINTS.GET_LOBBY}?lobbyId=${encodeURIComponent(id)}`,
            getLobbyByInvite: (code) => API_ENDPOINTS.GET_LOBBY_BY_INVITE(code),
            leaveLobby: (id) => `${API_ENDPOINTS.LEAVE_LOBBY}?lobbyId=${encodeURIComponent(id)}`,
            closeLobby: (id) => `${API_ENDPOINTS.CLOSE_LOBBY}?lobbyId=${encodeURIComponent(id)}`,
            startGame: API_ENDPOINTS.START_GAME,
            status: (id) => `${API_ENDPOINTS.STATUS}?lobbyId=${encodeURIComponent(id)}`
        };
    }, []);

    const lobbyInvite = normalizeInviteCode(lobbyData?.inviteCode);
    const lobbyChatDisabled = !(lobbyInvite && lobbyInvite.length === 4);

    useEffect(() => {
        try {
            const raw = localStorage.getItem('chess-settings');
            if (!raw) return;
            const s = JSON.parse(raw);
            if (!s) return;

            const squareSize = Number(s.squareSize);
            const pieceFontSize = Number(s.pieceFontSize);
            const timerMinutes = Number(s.timerMinutes);
            const layoutMode = String(s.layoutMode || '');

            setSettings((prev) => ({
                ...prev,
                squareSize: Number.isFinite(squareSize) ? squareSize : prev.squareSize,
                pieceFontSize: Number.isFinite(pieceFontSize) ? pieceFontSize : prev.pieceFontSize,
                timerMinutes: Number.isFinite(timerMinutes) ? timerMinutes : prev.timerMinutes,
                layoutMode: layoutMode ? layoutMode : prev.layoutMode
            }));
        } catch { }
    }, []);

    const loadSettings = () => {
        try {
            const raw = localStorage.getItem('chess-settings');
            if (!raw) return;
            const s = JSON.parse(raw);
            if (!s) return;

            const squareSize = Number(s.squareSize);
            const pieceFontSize = Number(s.pieceFontSize);
            const timerMinutes = Number(s.timerMinutes);
            const layoutMode = String(s.layoutMode || '');

            setSettings((prev) => ({
                ...prev,
                squareSize: Number.isFinite(squareSize) ? squareSize : prev.squareSize,
                pieceFontSize: Number.isFinite(pieceFontSize) ? pieceFontSize : prev.pieceFontSize,
                timerMinutes: Number.isFinite(timerMinutes) ? timerMinutes : prev.timerMinutes,
                layoutMode: layoutMode ? layoutMode : prev.layoutMode
            }));
        } catch { }
    };

    useEffect(() => {
        const token = localStorage.getItem('JWToken');
        if (!token) {
            setMyUserId(null);
            setMyUserName(null);
            return;
        }

        try {
            const payload = parseJwt(token);
            const id = payload.sub || payload.id || payload.nameid || payload["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"];
            const name = payload.name || payload.unique_name || payload["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name"];

            setMyUserId(id ? String(id) : null);
            setMyUserName(name ? String(name) : null);
        } catch {
            setMyUserId(null);
            setMyUserName(null);
        }
    }, [isRunning]);

    useInterval(async () => {
        if (!isRunning) return;

        try {
            const res = await onlinePing();
            setPingStatus(res || 'OK');
        } catch (e) {
            setPingStatus(e?.message || 'Ping error');
        }
    }, isRunning ? 6000 : null);

    useEffect(() => {
        if (!isRunning) return;
        if (chatView !== 'lobby') return;
        if (lobbyChatDisabled) {
            setChatView('global');
        }
    }, [lobbyChatDisabled, chatView]);

    useEffect(() => {
        const currentLobbyId = sessionStorage.getItem("currentLobbyId");
        if (!((activeContent === "lobby-status" || activeContent === "game") && currentLobbyId)) {
            return;
        }

        const token = getRawToken();
        let cancelled = false;
        let timeoutId = null;

        const pollData = async () => {
            if (cancelled) return;

            try {
                if (activeContent === "lobby-status") {
                    try {
                        const lobbyUrl = endpoints.getLobbyById(currentLobbyId);
                        const lobbyResponse = await axios.get(lobbyUrl, {
                            headers: { Authorization: token ? `Bearer ${token}` : '' }
                        });

                        const normalized = normalizeLobbyInfo(lobbyResponse.data);
                        setLobbyData(normalized);

                        const waiting = isLobbyWaiting(normalized?.status);
                        if (!waiting) {
                            try {
                                const statusUrl = endpoints.status(currentLobbyId);
                                const gameResponse = await axios.get(statusUrl, {
                                    headers: { Authorization: token ? `Bearer ${token}` : '' },
                                });

                                if (gameResponse.status === 200 && gameResponse.data) {
                                    setInitialGameState(gameResponse.data);
                                    setMoveHistory([]);
                                    setActiveContent("game");
                                    setChatView('lobby');
                                    return;
                                }
                            } catch { }
                        }
                    } catch (e) {
                        const httpStatus = e?.response?.status;
                        if (httpStatus === 404) {
                            setActiveContent("menu");
                            setErrorMsg("Lobby not found.");
                            setChatView('global');
                            sessionStorage.removeItem('currentLobbyId');
                            return;
                        }
                    }
                }

                if (activeContent === "game") {
                    try {
                        const statusUrl = endpoints.status(currentLobbyId);
                        const gameResponse = await axios.get(statusUrl, {
                            headers: { Authorization: token ? `Bearer ${token}` : '' },
                        });

                        if (gameResponse.status === 200 && gameResponse.data) {
                            setInitialGameState(gameResponse.data);
                        }

                        const lobbyUrl = endpoints.getLobbyById(currentLobbyId);
                        const lobbyResponse = await axios.get(lobbyUrl, {
                            headers: { Authorization: token ? `Bearer ${token}` : '' }
                        });
                        
                        const lData = normalizeLobbyInfo(lobbyResponse.data);
                        setLobbyData(lData); 

                        if (lData && myUserId) {
                            const isHost = String(lData.creator).toLowerCase() === String(myUserId).toLowerCase();
        
                            const hasOpponent = lData.joined && !isEmptyGuid(lData.joined);

                            if (isHost && !hasOpponent) {
                          
                                try {
                                    await axios.delete(
                                        endpoints.closeLobby(currentLobbyId),
                                        { headers: { Authorization: token ? `Bearer ${token}` : '' } }
                                    );
                                } catch (e) { 
                                    console.error("Auto-close lobby failed:", e);
                                }

                           
                                setActiveContent("menu");
                                setErrorMsg("Opponent left the match. Lobby closed.");
                                setChatView('global');
                                sessionStorage.removeItem('currentLobbyId');
                                setInitialGameState(null);
                                setLobbyData(null);
                                return;
                            }
                        }

                    } catch (e) {
                        const httpStatus = e?.response?.status;
                        if (httpStatus === 404) {
                            setActiveContent("menu");
                            setErrorMsg("Lobby is no longer available.");
                            setChatView('global');
                            sessionStorage.removeItem('currentLobbyId');
                            return;
                        }
                    }
                }
            } catch (error) {
                console.error("Polling error:", error);
            } finally {
                if (!cancelled) {
                    timeoutId = setTimeout(pollData, 1000);
                }
            }
        };

        pollData();

        return () => {
            cancelled = true;
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [activeContent, endpoints, myUserId]);

    useEffect(() => {
        if (activeContent !== 'join-lobby') return;

        const code = normalizeInviteCode(joinCode);

        setJoinPreviewError('');
        if (errorMsg) setErrorMsg('');

        if (code.length !== 4) {
            setJoinPreviewCode(code);
            setJoinPreviewLobby(null);
            setJoinPreviewCreator(null);
            setJoinPreviewJoined(null);
            setJoinPreviewLoading(false);
            return;
        }

        const reqId = ++joinPreviewReqRef.current;
        const token = getRawToken();

        setJoinPreviewCode(code);
        setJoinPreviewLoading(true);
        setJoinPreviewLobby(null);
        setJoinPreviewCreator(null);
        setJoinPreviewJoined(null);
        setJoinPreviewError('');

        const timeoutId = setTimeout(async () => {
            try {
                const lobbyRes = await axios.get(endpoints.getLobbyByInvite(code), {
                    headers: { Authorization: token ? `Bearer ${token}` : '' }
                });

                if (reqId !== joinPreviewReqRef.current) return;

                const lobby = normalizeLobbyInfo(lobbyRes.data);
                setJoinPreviewLobby(lobby);

                const status = String(lobby?.status ?? '');
                const isWaiting = isLobbyWaiting(status);

                if (!lobby?.lobbyId || !lobby?.inviteCode) {
                    setJoinPreviewError('Lobby not found.');
                    setJoinPreviewLoading(false);
                    return;
                }

                if (!isWaiting) {
                    setJoinPreviewError('Lobby is not joinable right now.');
                    setJoinPreviewLoading(false);
                    return;
                }

                const creatorUid = lobby?.creator;
                const joinedUid = lobby?.joined;

                const getUser = async (uid) => {
                    if (!uid || isEmptyGuid(uid)) return null;
                    try {
                        const url = API_ENDPOINTS.GET_PUBLIC_USER(uid);
                        const res = await axios.get(url, {
                            headers: { Authorization: token ? `Bearer ${token}` : '' }
                        });
                        return normalizePublicUser(res.data);
                    } catch {
                        return null;
                    }
                };

                const c = await getUser(creatorUid);
                const j = await getUser(joinedUid);

                if (reqId !== joinPreviewReqRef.current) return;

                setJoinPreviewCreator(c);
                setJoinPreviewJoined(j);

                if (joinedUid && !isEmptyGuid(joinedUid)) {
                    setJoinPreviewError('Lobby already has a joined player.');
                    setJoinPreviewLoading(false);
                    return;
                }

                setJoinPreviewError('');
                setJoinPreviewLoading(false);
            } catch (e) {
                if (reqId !== joinPreviewReqRef.current) return;
                const httpStatus = e?.response?.status;
                if (httpStatus === 404) setJoinPreviewError('Lobby not found.');
                else setJoinPreviewError('Failed to load lobby preview.');
                setJoinPreviewLoading(false);
                setJoinPreviewLobby(null);
                setJoinPreviewCreator(null);
                setJoinPreviewJoined(null);
            }
        }, 200);

        return () => clearTimeout(timeoutId);
    }, [joinCode, activeContent]);

    const isJoinPreviewValid = useMemo(() => {
        const code = normalizeInviteCode(joinCode);
        if (code.length !== 4) return false;
        if (joinPreviewLoading) return false;
        if (!joinPreviewLobby) return false;
        if (joinPreviewCode !== code) return false;

        const status = String(joinPreviewLobby?.status ?? '');
        const waiting = isLobbyWaiting(status);
        if (!waiting) return false;

        const joinedUid = joinPreviewLobby?.joined;
        if (joinedUid && !isEmptyGuid(joinedUid)) return false;

        return true;
    }, [joinCode, joinPreviewLobby, joinPreviewLoading, joinPreviewCode]);

    async function handleCreateLobby() {
        setIsLoading(true);
        setErrorMsg('');
        setInitialGameState(null);

        try {
            const response = await axios.get(endpoints.createLobby, getAuthHeader());
            const data = normalizeLobbyInfo(response.data);

            if (data) {
                setLobbyData(data);
                if (data.lobbyId) {
                    sessionStorage.setItem('currentLobbyId', data.lobbyId);
                    sessionStorage.setItem("hostState", "1");
                }
            }

            setActiveContent('lobby-status');
            setChatView('lobby');
            setShowLeftCanvas(false); 
        } catch (error) {
            const backendMsg = error?.response?.data?.message || error?.response?.data;
            setErrorMsg(backendMsg || 'Failed to create lobby.');
        } finally {
            setIsLoading(false);
        }
    }

    async function handleJoinLobby() {
        const code = normalizeInviteCode(joinCode);

        if (code.length !== 4) {
            setErrorMsg("Please enter a 4-character invite code.");
            return;
        }

        if (joinPreviewLoading) {
            setErrorMsg("Checking lobby info...");
            return;
        }

        if (!joinPreviewLobby || joinPreviewCode !== code || !isJoinPreviewValid) {
            setErrorMsg(joinPreviewError || "This lobby is not joinable.");
            return;
        }

        setIsLoading(true);
        setErrorMsg('');
        setInitialGameState(null);

        try {
            const response = await axios.get(endpoints.joinLobby(code), getAuthHeader());
            const data = normalizeLobbyInfo(response.data);

            if (data) {
                setLobbyData(data);
                if (data.lobbyId) {
                    sessionStorage.setItem('currentLobbyId', data.lobbyId);
                    sessionStorage.setItem("hostState", "0");
                }
            }

            setActiveContent('lobby-status');
            setChatView('lobby');
            setShowLeftCanvas(false);
        } catch (error) {
            const backendMsg = error?.response?.data?.message || error?.response?.data;
            setErrorMsg(backendMsg || 'Failed to join lobby. Invalid code?');
        } finally {
            setIsLoading(false);
        }
    }

    async function leaveLobby() {
        const token = getRawToken();
        const lobbyId = sessionStorage.getItem('currentLobbyId');
        if (!lobbyId) return;

        try {
            await axios.post(
                endpoints.leaveLobby(lobbyId),
                {},
                { headers: { Authorization: token ? `Bearer ${token}` : '' } }
            );
            sessionStorage.removeItem('currentLobbyId');
        } catch { }
    }

    async function closeLobby() {
        const token = getRawToken();
        const lobbyId = sessionStorage.getItem('currentLobbyId');
        if (!lobbyId) return;

        try {
            await axios.delete(
                endpoints.closeLobby(lobbyId),
                { headers: { Authorization: token ? `Bearer ${token}` : '' } }
            );
            sessionStorage.removeItem('currentLobbyId');
        } catch { }
    }

    const saveToReplays = () => {
        if (moveHistory.length > 0) {
            const replays = JSON.parse(localStorage.getItem('chess-replays') || '[]');
            const newReplay = {
                id: Date.now(),
                date: new Date().toLocaleString(),
                moves: moveHistory
            };
            localStorage.setItem('chess-replays', JSON.stringify([newReplay, ...replays].slice(0, 10)));
        }
    };

    const handleLeaveLobby = () => {
        if (activeContent === 'game') saveToReplays();

        setActiveContent('menu');

        const hostState = sessionStorage.getItem("hostState");
        try {
            if (hostState === "1") {
                closeLobby();
            } else if (hostState === "0") {
                leaveLobby();
            }
        } catch { }
        finally {
            setInitialGameState(null);
            setLobbyData(null);
            setChatView('global');
            setShowLeftCanvas(false);
        }
    };

    const startNewGame = async (startRequest) => {
        if (!lobbyData?.lobbyId) return;

        setIsLoading(true);
        setErrorMsg('');

        try {
            const payload = {
                lobbyId: lobbyData.lobbyId,
                whitePlayerUid: startRequest?.whitePlayerUid || lobbyData.creator,
                maxPlayerTimeMinutes: Number(startRequest?.maxPlayerTimeMinutes) || 0,
                startingFEN: String(startRequest?.startingFen || '').trim() || null
            };

            const response = await axios.post(endpoints.startGame, payload, getAuthHeader());

            if (response?.status === 200) {
                try {
                    const statusUrl = endpoints.status(lobbyData.lobbyId);
                    const gameResponse = await axios.get(statusUrl, getAuthHeader());
                    if (gameResponse.status === 200 && gameResponse.data) {
                        setInitialGameState(gameResponse.data);
                    }
                } catch { }

                setMoveHistory([]);
                setActiveContent('game');
                setChatView('lobby');
            }
        } catch (error) {
            const backendMsg = error?.response?.data?.message || error?.response?.data;
            setErrorMsg(backendMsg || "Failed to start game.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleTimeUp = () => { };

    const handleMove = useCallback((moveData) => {
        setMoveHistory(prev => {
            if (prev.length > 0) {
                const last = prev[prev.length - 1];
                if (last.from === moveData.from && last.to === moveData.to && last.color === moveData.color) {
                    return prev;
                }
            }
            return [...prev, moveData];
        });
    }, []);

    const isResponsive = settings.layoutMode === 'responsive';

    const renderUserLine = (iconClass, userInfo, fallbackUid) => {
        const uid = String(fallbackUid ?? '');
        const name = userInfo?.userName || (uid && !isEmptyGuid(uid) ? uid : 'Waiting for player...');
        const isMe = myUserId && uid && String(myUserId).toLowerCase() === uid.toLowerCase();
        const label = `${name}${isMe ? ' (You)' : ''}`;

        return (
            <div className="d-flex align-items-center mb-2">
                <i className={`${iconClass} me-2`}></i>
                <span className={isEmptyGuid(uid) ? "text-muted font-italic" : "text-white"} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {!isEmptyGuid(uid) && (
                        <span
                            title={userInfo?.isOnline ? 'Online' : 'Offline'}
                            style={{
                                width: 8,
                                height: 8,
                                borderRadius: 999,
                                display: 'inline-block',
                                backgroundColor: userInfo?.isOnline ? '#2ecc71' : 'rgba(255,255,255,0.35)'
                            }}
                        />
                    )}
                    {label}
                </span>
            </div>
        );
    };

    function leftRenderContent() {
        switch (activeContent) {
            case 'game':
                return (
                <div className="d-flex flex-column h-100">
    
                    <div className="text-center flex-shrink-0">
                        <h5 className="text-white">In Game</h5>
                        <p style={{ color: '#ddd' }}>{lobbyData ? `Lobby: ${lobbyData?.inviteCode}` : 'Quick Game'}</p>
                        <div className="d-grid gap-2 mt-3 mb-3">
                            <Button variant="outline-light" onClick={handleLeaveLobby}>Leave Game</Button>
                        </div>
                    </div>

                    <div className="flex-grow-1" style={{ minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                        {rightRenderContent(activeContent, "History")}
                    </div>
                </div>
            );

            case 'lobby-status':
                return (
                    <Lobby
                        lobbyData={lobbyData}
                        myUserId={myUserId}
                        onStartGame={startNewGame}
                        onLeave={handleLeaveLobby}
                        isLoading={isLoading}
                    />
                );

            case 'join-lobby': {
                const previewStatus = joinPreviewLobby ? pick(joinPreviewLobby, 'status') : null;
                const previewInvite = joinPreviewLobby ? normalizeInviteCode(pick(joinPreviewLobby, 'inviteCode')) : normalizeInviteCode(joinCode);
                const creatorUid = joinPreviewLobby ? pick(joinPreviewLobby, 'creator') : null;
                const joinedUid = joinPreviewLobby ? pick(joinPreviewLobby, 'joined') : null;

                const showPreview = normalizeInviteCode(joinCode).length === 4;

                return (
                    <div className="text-center px-2">
                        <h4 className="text-white">Join Game</h4>
                        <p className="text-muted small">Enter the 4-character code</p>

                        {errorMsg && <Alert variant="danger" className="py-2 small">{errorMsg}</Alert>}

                        <Form.Group className="mb-3">
                            <Form.Control
                                size="lg"
                                type="text"
                                placeholder="Code (e.g. 7DS9)"
                                className="text-center fw-bold text-uppercase"
                                value={joinCode}
                                maxLength={4}
                                onChange={(e) => setJoinCode(normalizeInviteCode(e.target.value))}
                            />
                        </Form.Group>

                        {showPreview && (
                            <div className="mb-3">
                                <div className="bg-dark p-3 rounded border border-secondary text-start">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div>
                                            <div className="text-muted" style={{ fontSize: '0.85rem' }}>Lobby Preview</div>
                                            <div style={{ fontSize: '1.25rem', fontWeight: 800, letterSpacing: 2, color: '#B6FFDA' }}>
                                                {previewInvite || normalizeInviteCode(joinCode)}
                                            </div>
                                        </div>

                                        <div className="text-end">
                                            {joinPreviewLoading ? (
                                                <div className="d-flex align-items-center gap-2">
                                                    <Spinner size="sm" animation="border" />
                                                    <span className="text-muted small">Loading...</span>
                                                </div>
                                            ) : (
                                                <span className={`badge ${isJoinPreviewValid ? 'bg-success' : (joinPreviewLobby ? 'bg-danger' : 'bg-secondary')}`}>
                                                    {isJoinPreviewValid ? 'Joinable' : (joinPreviewLobby ? 'Not joinable' : 'Unknown')}
                                                </span>
                                            )}
                                            {previewStatus && (
                                                <div className="small text-muted mt-1">Status: {String(previewStatus)}</div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="mt-3">
                                        <div className="text-muted border-bottom pb-2 mb-2" style={{ fontSize: '0.85rem' }}>Players</div>

                                        {joinPreviewLobby ? (
                                            <>
                                                {renderUserLine('bi bi-crown-fill text-warning', joinPreviewCreator, creatorUid)}
                                                {renderUserLine(
                                                    joinedUid && !isEmptyGuid(joinedUid) ? 'bi bi-person-check-fill text-success' : 'bi bi-person-dash text-secondary',
                                                    joinPreviewJoined,
                                                    joinedUid
                                                )}
                                            </>
                                        ) : (
                                            <div className="text-muted small">
                                                {joinPreviewError ? joinPreviewError : 'Type 4 characters to preview the lobby.'}
                                            </div>
                                        )}

                                        {joinPreviewError && (
                                            <Alert variant="warning" className="mt-3 mb-0 py-2 small">
                                                {joinPreviewError}
                                            </Alert>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="d-grid gap-2">
                            <Button
                                variant="primary"
                                className="menu-button txt-dark-green bg-light-green"
                                onClick={handleJoinLobby}
                                disabled={isLoading || joinPreviewLoading || !isJoinPreviewValid}
                            >
                                {isLoading ? 'Joining...' : (joinPreviewLoading ? 'Checking...' : 'Connect')}
                            </Button>
                            <Button
                                variant="outline-light"
                                onClick={() => {
                                    setActiveContent('menu');
                                    setErrorMsg('');
                                    setJoinCode('');
                                    setJoinPreviewCode('');
                                    setJoinPreviewLobby(null);
                                    setJoinPreviewCreator(null);
                                    setJoinPreviewJoined(null);
                                    setJoinPreviewLoading(false);
                                    setJoinPreviewError('');
                                }}
                            >
                                Back
                            </Button>
                        </div>
                    </div>
                );
            }

            case 'match-history':
                return (
                    <MatchHistory
                        onSelectReplay={(game) => { setSelectedReplay(game); setActiveContent('replay'); }}
                        onBack={() => setActiveContent('menu')}
                    />
                );

            case 'menu':
            default:
                return (
                    <div className="text-center">
                        <h3 className="text-white">Chess Game</h3>
                        <p className="text-muted">Select a game mode</p>

                        {errorMsg && <Alert variant="danger" className="py-1 small">{errorMsg}</Alert>}

                        <div className="d-grid gap-3 mt-4">
                            <Button
                                variant="primary"
                                size="lg"
                                onClick={handleCreateLobby}
                                disabled={isLoading}
                                className="menu-button txt-dark-green bg-light-green"
                            >
                                {isLoading ? 'Creating...' : 'Create Lobby'}
                            </Button>

                            <Button
                                variant="primary"
                                size="lg"
                                className="menu-button txt-dark-green bg-light-green"
                                onClick={() => setActiveContent('join-lobby')}
                            >
                                Join Game
                            </Button>

                            <Button
                                variant="primary"
                                size="lg"
                                className="menu-button txt-dark-green bg-light-green"

                                onClick={() => setActiveContent('match-history')}
                            >
                                Match History
                            </Button>

                            <Button
                                variant="outline-primary"
                                size="lg"
                                onClick={() => navigate('/mainmenu')}
                                className='bg-light-green txt-dark-green'
                            >
                                Back to Menu
                            </Button>
                        </div>
                    </div>
                );
        }
    }

    const middleRenderContent = () => {
        switch (activeContent) {
            case 'game':
                return (
                    <ChessGame
                        settings={settings}
                        lobbyId={lobbyData?.lobbyId}
                        myUserId={myUserId}
                        whitePlayerId={lobbyData?.creator}
                        serverStatus={initialGameState}
                        onMove={handleMove} 
                    />
                );

            case 'settings':
                return <SettingsDisplay onSettingsSaved={loadSettings} />;

            case 'replay':
                return <ReplayView replayData={selectedReplay} settings={settings} onBack={() => setActiveContent('match-history')} />;

            case 'lobby-status':
                return (
                    <div className="text-center text-white p-4">
                        <h4 style={{ color: '#B6FFDA' }} className="mb-2">Waiting for players...</h4>
                        <div className="text-muted small">Share your invite code from the left panel.</div>
                        <i className="bi bi-hourglass-split display-4" style={{ color: '#B6FFDA' }}></i>
                    </div>
                );

            case 'join-lobby':
            case 'menu':
            default:
                return (
                    <div className="text-center text-muted opacity-25">
                        <img
                            src={logo}
                            alt="Logo watermark"
                            style={{ maxWidth: '300px', filter: 'grayscale(100%)' }}
                        />
                    </div>
                );
        }
    };

    function rightRenderContent(activeContent, name) {
        if (name === "Timer") {
            if (activeContent !== 'game') return null;

            const status = initialGameState;
            const serverGameStatus = String(pick(status, 'gameStatus', 'GameStatus') ?? '');
            const isOngoing = serverGameStatus.toLowerCase() === 'ongoing';


            const whiteSeconds = pick(status, 'whiteTimeRemainingSeconds', 'WhiteTimeRemainingSeconds');
            const blackSeconds = pick(status, 'blackTimeRemainingSeconds', 'BlackTimeRemainingSeconds');
            const currentPlayer = pick(status, 'currentPlayer', 'CurrentPlayer');


            const isWhiteActive = isOngoing && String(currentPlayer).toLowerCase() === 'white';
            const isBlackActive = isOngoing && String(currentPlayer).toLowerCase() === 'black';

            return (
                <div className="d-flex flex-column gap-2 mt-3">
                    <Timer
                        totalSeconds={whiteSeconds}
                        isActive={isWhiteActive}
                        label="White"
                    />
                    <Timer
                        totalSeconds={blackSeconds}
                        isActive={isBlackActive}
                        label="Black"
                    />
                </div>
            );
        }

        if (name === "History") {
            if (activeContent !== 'game') return null;

            return (
                <div
                    className={`mt-3 p-2 border rounded border-secondary ${isResponsive ? 'flex-grow-1' : ''}`}
                    style={{
                        backgroundColor: 'rgba(0,0,0,0.2)',
                        maxHeight: isResponsive ? 'none' : '150px',
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    <h6 className="text-muted text-center mb-2">Moves</h6>

                    <div
                        className="no-scrollbar"
                        style={{
                            overflowY: 'auto',
                            minHeight: '75px',
                            maxHeight: '300px',
                        }}
                    >
                        <Table size="sm" striped bordered hover variant="dark" className="text-center mb-0" style={{ fontSize: '0.9rem' }}>
                            <thead style={{ position: 'sticky', top: 0, backgroundColor: '#212529', zIndex: 1 }}>
                                <tr>
                                    <th style={{ width: '20%' }}>#</th>
                                    <th style={{ width: '40%' }}>Player</th>
                                    <th style={{ width: '40%' }}>Move</th>
                                </tr>
                            </thead>
                            <tbody>
                                {moveHistory.map((moveData, index) => {
                                    const isWhitePiece = moveData.color === 'w';

                                    return (
                                        <tr key={index}>
                                            <td className="text-white">{index + 1}.</td>
                                            <td style={{ color: isWhitePiece ? '#fff' : '#aaa' }}>
                                                {isWhitePiece ? (
                                                    <span><i className="bi bi-circle-fill me-1 text-light"></i> White</span>
                                                ) : (
                                                    <span><i className="bi bi-circle-fill me-1 text-secondary"></i> Black</span>
                                                )}
                                            </td>

                                            <td className={moveData.isCapture ? 'text-danger fw-bold' : ''}>
                                                {moveData.san}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>

                        {moveHistory.length === 0 && (
                            <div className="text-center text-muted mt-3 fst-italic">
                                No moves yet.
                            </div>
                        )}
                    </div>
                </div>
            );
        }

        if (name === "Chat") {
            if (chatView === 'lobby' && !lobbyChatDisabled) {
                return <LobbyChat invite={lobbyInvite} lobby={lobbyData} />;
            }
            return <Chat name={name} />;
        }

        return <Chat name={name} />;
    }

    const containerClass = isResponsive
        ? "bg-chess vh-100 d-flex flex-column overflow-hidden"
        : "bg-chess min-vh-100 d-flex align-items-center";

    const innerContainerStyle = isResponsive ? { marginTop: 0 } : { marginTop: "5%", marginBottom: "auto" };
    const innerContainerClass = isResponsive ? "h-100 py-3" : "";

    const cardStyle = {
        transform: "none",
        height: isMobile ? "100%" : (isResponsive ? "100%" : "60rem")
    };

    return (
        <Container fluid className={containerClass}>
            
            {/* --- MOBILE NAVBAR --- */}
            {isMobile && (
                <Navbar bg="dark" variant="dark" className="border-bottom border-secondary px-3 flex-shrink-0 justify-content-between">
                    {activeContent === 'game' ? (
                         <Button variant="outline-light" size="sm" onClick={() => setShowLeftCanvas(true)}>
                            <i className="bi bi-list"></i> Info
                         </Button>
                    ) : (
                        <div style={{width: 32}}></div> 
                    )}
                   
                    <Navbar.Brand className="mx-auto" style={{fontSize: '1rem'}}>EvoSquare</Navbar.Brand>
                    
                    <Button variant="outline-info" size="sm" onClick={() => setShowRightCanvas(true)}>
                        <i className="bi bi-chat-dots"></i>
                    </Button>
                </Navbar>
            )}

            <Container fluid={isResponsive} style={isMobile ? { marginTop: 0 } : innerContainerStyle} className={`${innerContainerClass} ${isMobile ? 'flex-grow-1' : ''}`}>
                <Row className={`justify-content-center ${isResponsive ? 'h-100' : ''}`}>
                    <Col md={2} className={`d-none d-md-flex flex-column ${isResponsive ? 'h-100 mb-3 mb-md-0' : ''}`}>
                        <Card
                            className="shadow bg-dark-transparent text-white"
                            style={{ ...cardStyle, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
                        >
                            <Card.Header className="text-center flex-shrink-0" style={{ position: 'relative', zIndex: 2 }}>
                                <img
                                    style={{ maxHeight: "150px", width: "auto", maxWidth: "100%", display: 'block', margin: '0 auto' }}
                                    src={logo}
                                    alt="Chess Logo"
                                />
                            </Card.Header>

                            <Card.Body
                                className="d-flex flex-column"
                                style={{ flex: 1, minHeight: 0, overflowY: 'auto', position: 'relative', zIndex: 1 }}
                            >
                                {leftRenderContent()}
                        
                            </Card.Body>
                           
                            {(activeContent !== 'game' && activeContent !=='lobby-status')  && (
                                
                                <Card.Footer className="d-flex gap-2 flex-shrink-0">
                                    <Button
                                        variant="primary"
                                        className="flex-grow-1 menu-button txt-dark-green bg-light-green"
                                        size="lg"
                                        onClick={() => {
                                            navigate("/mainmenu")
                                            setErrorMsg('');
                                            setChatView('global');

                                        }}
                                    >
                                        Menu
                                    </Button>

                                    <Button
                                        variant="primary"
                                        className="menu-button txt-dark-green bg-light-green"
                                        size="lg"
                                        onClick={() => setActiveContent('settings')}
                                        style={{ aspectRatio: '1 / 1' }}
                                    >
                                        <i className="bi bi-gear"></i>
                                    </Button>

                               
                                </Card.Footer>
                            )}
                        </Card>
                    </Col>

                    <Col xs={12} md={7} className={`d-flex flex-column ${isResponsive ? 'h-100 mb-3 mb-md-0' : ''}`}>
                        <Card className="shadow bg-dark-transparent text-white" style={{ ...cardStyle, overflow: "hidden" }}>
                            <Card.Body className="d-flex align-items-center justify-content-center overflow-auto p-0">
                                {isMobile && activeContent !== 'game' && activeContent !== 'replay' && activeContent !== 'settings'
                                    ? leftRenderContent() 
                                    : middleRenderContent()
                                }
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col md={3} className={`d-none d-md-flex flex-column ${isResponsive ? 'h-100' : ''}`}>
                        <Card className="shadow bg-dark-transparent text-white" style={{ ...cardStyle, display: 'flex', flexDirection: 'column' }}>
                            <Card.Header className="flex-shrink-0 d-flex flex-column align-items-center justify-content-center">
                                <h5 className="mb-2 text-white">Game information</h5>
                                <div className="d-flex gap-2">
                                    <Button
                                        variant={chatView === 'lobby' ? 'primary' : 'outline-primary'}
                                        onClick={() => setChatView('lobby')}
                                        disabled={lobbyChatDisabled}
                                        title={lobbyChatDisabled ? 'No lobby yet (missing invite code)' : 'Lobby chat'}
                                        className="menu-button txt-dark-green bg-light-green"
                                        size="lg"
                                        style={{ aspectRatio: '1 / 1' }}
                                    >
                                        <i class="bi bi-people-fill"></i>
                                    </Button>
                                    <Button
                                        variant={chatView === 'global' ? 'primary' : 'outline-success'}
                                        onClick={() => setChatView('global')}
                                        title="Global chat"
                                        className="menu-button txt-dark-green bg-light-green"
                                        size="lg"
                                        style={{ aspectRatio: '1 / 1' }}
                                    >
                                        <i class="bi bi-globe-americas-fill"></i>
                                    </Button>
                                </div>
                            </Card.Header>
                            <Card.Body className="flex-grow-1 d-flex flex-column" style={{ minHeight: 0, overflow: 'hidden' }}>
                                {rightRenderContent(activeContent, "Chat")}
                                
                            </Card.Body>

                            <Card.Footer className="flex-shrink-0 small text-muted">
                                <div className="d-flex align-items-center justify-content-between">
                                    <span>{myUserName ? `You: ${myUserName}` : 'Not logged in'}</span>
                                    <span className={pingStatus ? 'text-success' : 'text-warning'}>
                                        {pingStatus ? 'Online' : '...'}
                                    </span>
                                </div>
                            </Card.Footer>
                        </Card>
                    </Col>

                </Row>
            </Container>

            <Offcanvas show={showLeftCanvas} onHide={() => setShowLeftCanvas(false)} placement="start" 
            className="bg-dark transparent text-white w-75"
            
            >
                <Offcanvas.Header closeButton closeVariant="white">
                    <Offcanvas.Title>Game Info</Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body className="d-flex flex-column">
                    {leftRenderContent()} 
                    
                </Offcanvas.Body>
            </Offcanvas>
            <Offcanvas show={showRightCanvas} onHide={() => setShowRightCanvas(false)} placement="end" className="bg-dark text-white w-85">
                <Offcanvas.Header closeButton closeVariant="white">
                    <Offcanvas.Title>Chat</Offcanvas.Title>
                    <div>
                        <Button size="sm" variant={chatView === 'lobby' ? 'primary' : 'outline-secondary'} onClick={() => setChatView('lobby')} disabled={lobbyChatDisabled} className="me-1">Lobby</Button>
                        <Button size="sm" variant={chatView === 'global' ? 'primary' : 'outline-success'} onClick={() => setChatView('global')}>Global</Button>
                    </div>
                </Offcanvas.Header>
                <Offcanvas.Body className="d-flex flex-column h-100 pb-3">
                     <div className="flex-grow-1 position-relative" style={{ minHeight: '300px' }}>
                        {rightRenderContent(activeContent, 'Chat')}
                     </div>
                </Offcanvas.Body>
            </Offcanvas>

        </Container>
    );
}

export default GameHub;